import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaFilter, FaSearch, FaSortAmountDown } from "react-icons/fa";
import { apiFetch } from "./api";
import UserModal from "./UserModal";
import { canonicalRole, uniqueFieldValues } from "./utils";

// Normalize to lowercase so legacy and new RSVP values both match
const presentStatuses = new Set(["available", "present"]);
const absentStatuses = new Set(["not available", "not_available", "absent", "late", "excused"]);

const toLocalYmd = (value = new Date()) => {
  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const CHUNK_DAYS = 30;
const MAX_TOTAL_COLUMNS = 365;
const INITIAL_VISIBLE_COLUMNS = 20;

const statusDisplay = (status) => {
  const norm = status ? String(status).toLowerCase() : "";
  if (presentStatuses.has(norm)) {
    return { icon: "✔", className: "present", label: "Available" };
  }
  if (absentStatuses.has(norm)) {
    return { icon: "✕", className: "absent", label: "Not Available" };
  }
  return { icon: "?", className: "pending", label: "No Response" };
};

const nextStatusValue = (current) => {
  const norm = current ? String(current).toLowerCase() : "";
  if (!norm) return "available";
  if (presentStatuses.has(norm)) return "not available";
  if (absentStatuses.has(norm)) return null;
  return "available";
};

const formatTime12Hour = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  const h = parseInt(hours, 10);
  if (isNaN(h)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${minutes || "00"} ${period}`;
};

const formatPracticeTimeRange = (startTime, endTime) => {
  const startLabel = startTime ? formatTime12Hour(startTime) : "";
  const endLabel = endTime && endTime !== startTime ? formatTime12Hour(endTime) : "";
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel;
};

const formatShortDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatShortDateWithDay = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const weekday = date
    .toLocaleDateString(undefined, { weekday: "short" })
    .toUpperCase();
  const md = formatShortDate(value);
  return `${weekday} ${md}`.trim();
};

const truncateLabel = (value, maxLength) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}…`;
};

const normalizePracticeType = (practice) => {
  const candidates = [
    practice?.type,
    practice?.practiceType,
    practice?.practice_type,
    practice?.state,
    practice?.practice_state,
    practice?.eventType,
    practice?.event_type,
    practice?.category,
    practice?.kind,
  ];
  const raw = String(candidates.find((v) => v != null && String(v).trim()) || "")
    .trim()
    .toLowerCase();
  if (raw) {
    if (raw.includes("regatta")) return "regatta";
    if (raw.includes("event")) return "event";
    if (raw.includes("practice")) return "practice";
  }

  const name = String(practice?.name || "").trim().toLowerCase();
  const note = String(practice?.note || "").trim().toLowerCase();
  const location = String(practice?.location || "").trim().toLowerCase();
  const haystack = `${name} ${note} ${location}`.trim();

  if (haystack) {
    const regattaKeywords = [
      "regatta",
      "classic",
      "championship",
      "championships",
      "cup",
      "invitational",
      "invite",
      "duel",
      "dual",
      "scrimmage",
      "race",
    ];
    if (regattaKeywords.some((kw) => haystack.includes(kw))) return "regatta";
    if (haystack.includes("event")) return "event";
  }

  return "practice";
};

const practiceLabel = (practice) => {
  const date = practice?.date ? new Date(`${practice.date}T00:00:00`) : null;
  const day = date ? date.toLocaleDateString(undefined, { weekday: "short" }) : "";
  const md = date ? date.toLocaleDateString(undefined, { month: "numeric", day: "numeric" }) : "";
  const time = formatPracticeTimeRange(practice?.startTime || practice?.time || "", practice?.endTime || "");
  const normalizedType = normalizePracticeType(practice);

  if (normalizedType === "regatta") {
    const nameLabel = truncateLabel(practice?.abbrevName || practice?.name || "Regatta", 10);
    const startDate = practice?.startDate || practice?.date || "";
    const endDate = practice?.endDate || "";
    const startLabel = formatShortDateWithDay(startDate);
    const endLabel = formatShortDateWithDay(endDate);
    const rangeLabel = endLabel ? `${startLabel} – ${endLabel}` : startLabel;
    return {
      day: nameLabel,
      md: rangeLabel,
      time: "",
      title: `${nameLabel}${rangeLabel ? ` • ${rangeLabel}` : ""}`.trim(),
    };
  }

  return {
    day,
    md,
    time,
    title: `${day || ""} ${md || ""}${time ? ` • ${time}` : ""}`.trim(),
  };
};

const addDays = (dateStr, delta) => {
  const base = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(base.getTime())) return toLocalYmd();
  base.setDate(base.getDate() + delta);
  return toLocalYmd(base);
};

const mergeAttendanceRecords = (rows, prevMap = {}) => {
  const next = { ...prevMap };
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const practiceId = Number(row.practice_id);
    const userId = Number(row.user_id);
    if (!practiceId || !userId) return;
    const normalized = row.status ? String(row.status).toLowerCase() : "";
    if (!next[practiceId]) next[practiceId] = {};
    next[practiceId][userId] = normalized || null;
  });
  return next;
};

const inclusiveDayDiff = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  const diff = Math.floor((e - s) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff + 1 : 0;
};

export default function RosterAttendance({
  roster = [],
  coach,
  rosterLoading = false,
  rosterError = "",
  onRosterUpdated,
}) {
  const [teamFilter, setTeamFilter] = useState(() => coach?.team || "all");
  const [squadFilter, setSquadFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [ageGroupFilter, setAgeGroupFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [configuredTeams, setConfiguredTeams] = useState([]);
  const [configuredSquads, setConfiguredSquads] = useState([]);
  const [configuredPositions, setConfiguredPositions] = useState([]);
  const [highSchools, setHighSchools] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 640px), (max-height: 480px)").matches;
  });
  const [showSearch, setShowSearch] = useState(false);
  const [showSort, setShowSort] = useState(false);

  // Per-column filter and sort state: { practiceId: { filter: 'all'|'present'|'absent'|'pending', sort: 'none'|'asc'|'desc' } }
  const [columnControls, setColumnControls] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);

  const [practiceTypeFilter, setPracticeTypeFilter] = useState("all");
  const [dateRangeFrom, setDateRangeFrom] = useState(() => toLocalYmd());
  const [dateRangeTo, setDateRangeTo] = useState("");
  const [rangeStart, setRangeStart] = useState(() => toLocalYmd());
  const [rangeEnd, setRangeEnd] = useState(null);
  const [practices, setPractices] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [attendanceError, setAttendanceError] = useState("");
  const [savingCell, setSavingCell] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreAhead, setHasMoreAhead] = useState(true);
  const [loadedEnd, setLoadedEnd] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const tableRef = useRef(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 640px), (max-height: 480px)");
    const handler = () => setIsMobile(query.matches);
    handler();
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", handler);
      return () => query.removeEventListener("change", handler);
    }
    query.addListener(handler);
    return () => query.removeListener(handler);
  }, []);

  // Load picklists for filters
  useEffect(() => {
    async function loadPicklists() {
      try {
        const [teamsData, squadsData, positionsData, highSchoolsData] = await Promise.all([
          apiFetch("/teams"),
          apiFetch("/squads"),
          apiFetch("/positions"),
          apiFetch("/high-schools"),
        ]);
        setConfiguredTeams(teamsData.map((t) => t.name).sort((a, b) => a.localeCompare(b)));
        setConfiguredSquads(squadsData.map((s) => s.name).sort((a, b) => a.localeCompare(b)));
        setConfiguredPositions(positionsData.map((p) => p.name).sort((a, b) => a.localeCompare(b)));
        setHighSchools(Array.isArray(highSchoolsData) ? highSchoolsData.map((h) => h.name).sort((a, b) => a.localeCompare(b)) : []);
      } catch (e) {
        console.warn("Failed to load picklists for attendance tab:", e);
      }
    }
    loadPicklists();
  }, []);

  const fallbackTeams = useMemo(() => uniqueFieldValues(roster, "team"), [roster]);
  const teams = configuredTeams.length > 0 ? configuredTeams : fallbackTeams;

  const fallbackSquads = useMemo(() => uniqueFieldValues(roster, "squad"), [roster]);
  const squads = configuredSquads.length > 0 ? configuredSquads : fallbackSquads;

  const fallbackPositions = useMemo(() => uniqueFieldValues(roster, "position"), [roster]);
  const positions = configuredPositions.length > 0 ? configuredPositions : fallbackPositions;

  const ageGroups = useMemo(() => uniqueFieldValues(roster, "age_group"), [roster]);

  const fetchMatrixChunk = useCallback(
    async (fromDate, span) => {
      const params = new URLSearchParams({
        from: fromDate,
        days: String(span),
        limit: String(span * 5), // allow up to 5 practices per day
      });
      const effectiveTeam = teamFilter !== "all" ? teamFilter : (coach?.team || "");
      if (effectiveTeam) params.append("team", effectiveTeam);
      return apiFetch(`/attendance/matrix?${params.toString()}`);
    },
    [coach?.team, teamFilter]
  );

  const loadMatrix = useCallback(async () => {
    setLoadingAttendance(true);
    setAttendanceError("");
    setHasMoreAhead(true);
    setLoadedEnd(null);
    try {
      if (rangeEnd) {
        // Custom date range mode: load everything from rangeStart to rangeEnd in one shot
        const span = inclusiveDayDiff(rangeStart, rangeEnd);
        const clampedSpan = Math.min(Math.max(span, 1), MAX_TOTAL_COLUMNS);
        const data = await fetchMatrixChunk(rangeStart, clampedSpan);
        const normalizedPractices = Array.isArray(data?.practices) ? data.practices : [];
        setPractices(normalizedPractices);
        setAttendanceMap(mergeAttendanceRecords(data?.records));
        setLoadedEnd(rangeEnd);
        setHasMoreAhead(false);
      } else {
        // Default mode: load forward from today
        const span = Math.min(90, MAX_TOTAL_COLUMNS);
        const chunkEnd = addDays(rangeStart, span - 1);
        const data = await fetchMatrixChunk(rangeStart, span);
        const normalizedPractices = Array.isArray(data?.practices) ? data.practices : [];
        setPractices(normalizedPractices);
        setAttendanceMap(mergeAttendanceRecords(data?.records));
        setLoadedEnd(chunkEnd);
        const daysFetched = inclusiveDayDiff(rangeStart, chunkEnd);
        setHasMoreAhead(daysFetched < MAX_TOTAL_COLUMNS);
      }
    } catch (e) {
      setAttendanceError(e?.message || "Failed to load attendance status");
      setPractices([]);
      setAttendanceMap({});
      setHasMoreAhead(false);
    } finally {
      setLoadingAttendance(false);
    }
  }, [fetchMatrixChunk, rangeStart, rangeEnd]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const loadMorePractices = useCallback(async () => {
    if (!hasMoreAhead || loadingMore) return;
    const currentEnd = loadedEnd || rangeStart;
    let daysFetched = inclusiveDayDiff(rangeStart, currentEnd);
    let remainingDays = MAX_TOTAL_COLUMNS - daysFetched;
    if (remainingDays <= 0) {
      setHasMoreAhead(false);
      return;
    }
    let nextSpan = Math.min(CHUNK_DAYS, remainingDays);
    let nextStart = loadedEnd ? addDays(loadedEnd, 1) : rangeStart;
    let nextEnd = addDays(nextStart, nextSpan - 1);
    setLoadingMore(true);
    try {
      let updatedPractices = practices;
      let foundData = false;

      while (remainingDays > 0) {
        const data = await fetchMatrixChunk(nextStart, nextSpan);
        const newPractices = Array.isArray(data?.practices) ? data.practices : [];
        const existingIds = new Set(updatedPractices.map((p) => p.id));
        const filtered = newPractices.filter((p) => !existingIds.has(p.id));
        if (filtered.length) {
          updatedPractices = [...updatedPractices, ...filtered];
          setPractices(updatedPractices);
          foundData = true;
        }
        const hasRecords = Array.isArray(data?.records) && data.records.length;
        if (hasRecords) {
          setAttendanceMap((prev) => mergeAttendanceRecords(data.records, prev));
          foundData = true;
        }

        setLoadedEnd(nextEnd);
        daysFetched = inclusiveDayDiff(rangeStart, nextEnd);
        remainingDays = MAX_TOTAL_COLUMNS - daysFetched;
        if (foundData || remainingDays <= 0) break;

        nextStart = addDays(nextEnd, 1);
        nextSpan = Math.min(CHUNK_DAYS, remainingDays);
        nextEnd = addDays(nextStart, nextSpan - 1);
      }

      setHasMoreAhead(remainingDays > 0);
    } catch (e) {
      console.error("Failed to load more attendance columns:", e);
      setHasMoreAhead(false);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMatrixChunk, hasMoreAhead, loadedEnd, loadingMore, practices, rangeStart]);

  const updateAttendanceStatus = useCallback(
    async (practiceId, userId, isCoach) => {
      const key = `${practiceId}-${userId}`;
      const currentStatus = attendanceMap[practiceId]?.[userId] || null;
      const next = nextStatusValue(currentStatus);
      setSavingCell(key);
      setAttendanceError("");
      try {
        if (isCoach) {
          await apiFetch("/attendance/coach/coach", {
            method: "POST",
            body: JSON.stringify({
              practice_id: practiceId,
              coach_id: userId,
              status: next,
            }),
          });
        } else {
          await apiFetch("/attendance/coach/athlete", {
            method: "POST",
            body: JSON.stringify({
              practice_id: practiceId,
              athlete_id: userId,
              status: next,
            }),
          });
        }
        setAttendanceMap((prev) => {
          const nextMap = { ...prev };
          const practiceStatuses = { ...(nextMap[practiceId] || {}) };
          if (next) {
            practiceStatuses[userId] = next;
            nextMap[practiceId] = practiceStatuses;
          } else {
            delete practiceStatuses[userId];
            if (Object.keys(practiceStatuses).length) {
              nextMap[practiceId] = practiceStatuses;
            } else {
              delete nextMap[practiceId];
            }
          }
          return nextMap;
        });
      } catch (e) {
        setAttendanceError(e?.message || "Failed to update attendance");
      } finally {
        setSavingCell(null);
      }
    },
    [attendanceMap]
  );

  const exportAttendanceCsv = useCallback(async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    setAttendanceError("");
    try {
      let from = rangeStart;
      let to = loadedEnd || rangeStart;
      const practiceDates = practices
        .map((p) => p?.date)
        .filter(Boolean)
        .sort();
      if (practiceDates.length > 0) {
        from = practiceDates[0];
        to = practiceDates[practiceDates.length - 1];
      }
      if (from && to && from > to) {
        const temp = from;
        from = to;
        to = temp;
      }
      const params = new URLSearchParams({ from, to });
      const csvText = await apiFetch(`/attendance/export/csv?${params.toString()}`);
      const blob = new Blob([csvText || ""], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `attendance-export-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setAttendanceError(e?.message || "Failed to export attendance");
    } finally {
      setExportingCsv(false);
    }
  }, [exportingCsv, loadedEnd, practices, rangeStart]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (!hasMoreAhead || loadingMore) return;
      const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
      if (remaining < 80) {
        loadMorePractices();
      }
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, [hasMoreAhead, loadMorePractices, loadingMore]);

  useEffect(() => {
    const el = tableRef.current;
    if (!el || loadingAttendance || loadingMore) return;
    if (!hasMoreAhead) return;
    if (el.scrollWidth <= el.clientWidth + 8) {
      loadMorePractices();
    }
  }, [hasMoreAhead, loadMorePractices, loadingAttendance, loadingMore, practices.length, loadedEnd]);

  useEffect(() => {
    if (loadingAttendance || loadingMore) return;
    if (!hasMoreAhead) return;
    if (practices.length >= INITIAL_VISIBLE_COLUMNS) return;
    loadMorePractices();
  }, [hasMoreAhead, loadMorePractices, loadingAttendance, loadingMore, practices.length, loadedEnd]);

  const sortValueFor = (record, field) => {
    if (!record) return "";
    const raw = field === "name" ? record.name : record[field];
    return (raw ?? "").toString().trim().toLowerCase();
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown !== null) {
        const dropdownEl = dropdownRefs.current[activeDropdown];
        if (dropdownEl && !dropdownEl.contains(e.target)) {
          setActiveDropdown(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  const displayedRoster = useMemo(() => {
    const tq = q.trim().toLowerCase();

    // First apply roster-level filters
    let filtered = roster.filter((r) => {
      const isActive = r.is_active !== false && r.is_active !== 0;
      if (!isActive) return false;
      const roleKey = canonicalRole(r);
      const tOk =
        teamFilter === "all" ||
        (r.team || "").trim().toLowerCase() === teamFilter.trim().toLowerCase();
      const sOk =
        squadFilter === "all" ||
        (r.squad || "").trim().toLowerCase() === squadFilter.trim().toLowerCase();
      const agOk =
        ageGroupFilter === "all" ||
        (r.age_group || "").trim().toLowerCase() === ageGroupFilter.trim().toLowerCase();
      const qOk = !tq || (r.name || "").toLowerCase().includes(tq);
      const roleOk = roleFilter === "all" || roleKey === roleFilter;
      return tOk && sOk && agOk && qOk && roleOk;
    });

    // Apply column-specific filters
    Object.keys(columnControls).forEach((practiceId) => {
      const control = columnControls[practiceId];
      if (control?.filter && control.filter !== "all") {
        filtered = filtered.filter((r) => {
          const userId = r.user_id ?? r.id;
          const statuses = attendanceMap[practiceId] || {};
          const status = statuses[userId];

          if (control.filter === "available") {
            return status && presentStatuses.has(status);
          } else if (control.filter === "notavailable") {
            return status && absentStatuses.has(status);
          } else if (control.filter === "noresponse") {
            return !status;
          }
          return true;
        });
      }
    });

    // Apply sorting - first by column-specific sort, then by global sort
    const columnSortEntry = Object.entries(columnControls).find(
      ([, control]) => control?.sort && control.sort !== "none"
    );

    if (columnSortEntry) {
      const [practiceId, control] = columnSortEntry;
      filtered = [...filtered].sort((a, b) => {
        const aUserId = a.user_id ?? a.id;
        const bUserId = b.user_id ?? b.id;
        const statuses = attendanceMap[practiceId] || {};
        const aStatus = statuses[aUserId] || null;
        const bStatus = statuses[bUserId] || null;

        // Sort order: present > absent > pending/other
        const statusValue = (status) => {
          if (!status) return 0;
          if (presentStatuses.has(status)) return 3;
          if (absentStatuses.has(status)) return 2;
          return 1;
        };

        const aVal = statusValue(aStatus);
        const bVal = statusValue(bStatus);

        const diff = control.sort === "asc" ? aVal - bVal : bVal - aVal;
        if (diff !== 0) return diff;

        // Secondary sort by name
        return (a.name || "").localeCompare(b.name || "");
      });
    } else {
      // Apply global sort
      filtered = [...filtered].sort((a, b) => {
        const aValue = sortValueFor(a, sortBy);
        const bValue = sortValueFor(b, sortBy);
        if (!aValue && bValue) return 1;
        if (aValue && !bValue) return -1;
        const primary = aValue.localeCompare(bValue);
        if (primary !== 0) return primary;
        return (a.name || "").localeCompare(b.name || "");
      });
    }

    return filtered;
  }, [roster, teamFilter, squadFilter, roleFilter, ageGroupFilter, q, sortBy, columnControls, attendanceMap]);

  const filteredPractices = useMemo(() => {
    return practices.filter((p) => {
      const pTeam = (p.team || "").trim().toLowerCase();
      const teamOk =
        teamFilter === "all" ||
        !pTeam ||
        pTeam === "all teams" ||
        pTeam === teamFilter.trim().toLowerCase();
      const pSquadNames = (p.squadNames || []).map((s) => s.trim().toLowerCase());
      const squadOk =
        squadFilter === "all" ||
        pSquadNames.length === 0 ||
        pSquadNames.includes(squadFilter.trim().toLowerCase());
      const typeOk =
        practiceTypeFilter === "all" ||
        normalizePracticeType(p) === practiceTypeFilter;
      return teamOk && squadOk && typeOk;
    });
  }, [practices, teamFilter, squadFilter, practiceTypeFilter]);

  const practiceStatuses = useMemo(() => {
    return filteredPractices.map((practice) => {
      const statuses = attendanceMap[practice.id] || {};
      const normalizedType = normalizePracticeType(practice);
      const typeClass =
        normalizedType === "event" || normalizedType === "regatta"
          ? `attendance-col-${normalizedType}`
          : "";

      // Calculate response counts: C = coxswain responses, R = rower responses (yes or no), NR = no response
      let coxResponses = 0;
      let rowerResponses = 0;
      let noResponses = 0;

      displayedRoster.forEach((athlete) => {
        const userId = athlete.user_id ?? athlete.id;
        if (canonicalRole(athlete) !== "athlete") return;

        const position = (athlete.position || "").trim().toUpperCase();
        const isCox = position.charAt(0) === "C";
        const status = statuses[userId];
        const hasResponse = !!status;

        if (!hasResponse) {
          noResponses += 1;
        } else if (isCox) {
          coxResponses += 1;
        } else {
          rowerResponses += 1;
        }
      });

      return {
        id: practice.id,
        info: practiceLabel(practice),
        statuses,
        responseCounts: { C: coxResponses, R: rowerResponses, NR: noResponses },
        typeClass,
      };
    });
  }, [attendanceMap, filteredPractices, displayedRoster]);

  const activeFilterCount = [
    teamFilter !== "all",
    squadFilter !== "all",
    roleFilter !== "all",
    ageGroupFilter !== "all",
  ].filter(Boolean).length;

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="lineup-pill-bar">
        <div className="lineup-pill-filters">
          {!isMobile && (
            <input
              className="input roster-search"
              placeholder="Search by name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
          {isMobile && (
            <button
              className={`btn-filter icon-only ${showSearch ? "active" : ""}`}
              onClick={() => {
                setShowSearch((prev) => !prev);
                setShowSort(false);
              }}
              title="Search"
              aria-label="Search"
            >
              <FaSearch />
            </button>
          )}
          <button
            className={`btn-filter ${isMobile ? "icon-only" : ""} ${showFilters ? "active" : ""}`}
            onClick={() => {
              setShowFilters(!showFilters);
              setShowSort(false);
            }}
            title="Filters"
            aria-label="Filters"
          >
            <FaFilter />
            {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
          </button>
          {isMobile ? (
            <button
              className={`btn-filter icon-only ${showSort ? "active" : ""}`}
              onClick={() => {
                setShowSort((prev) => !prev);
                setShowSearch(false);
              }}
              title="Sort"
              aria-label="Sort"
            >
              <FaSortAmountDown />
            </button>
          ) : (
            <select
              className="lineup-pill-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by name</option>
              <option value="team">Sort by team</option>
              <option value="squad">Sort by squad</option>
              <option value="age_group">Sort by age group</option>
              <option value="position">Sort by position</option>
            </select>
          )}
        </div>
        <div className="lineup-pill-actions">
          <button
            className="btn ghost"
            type="button"
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? "Done editing" : "Edit attendance"}
          </button>
          <button
            className="btn ghost"
            type="button"
            onClick={exportAttendanceCsv}
            disabled={exportingCsv}
            title="Export attendance CSV"
          >
            {exportingCsv ? "Exporting…" : "Export CSV"}
          </button>
        </div>
        {isMobile && showSearch && (
          <div className="roster-mini-panel">
            <input
              className="input roster-search roster-search-full"
              placeholder="Search by name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        )}
        {isMobile && showSort && (
          <div className="roster-mini-panel">
            <select
              className="lineup-pill-select roster-select-full"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by name</option>
              <option value="team">Sort by team</option>
              <option value="squad">Sort by squad</option>
              <option value="age_group">Sort by age group</option>
              <option value="position">Sort by position</option>
            </select>
          </div>
        )}
        {showFilters && (
          <div className="lineup-pill-filter-panel">
            <select
              className="lineup-pill-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All roles</option>
              <option value="athlete">Athletes</option>
              <option value="coach">Coaches</option>
            </select>
            <select
              className="lineup-pill-select"
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
            >
              <option value="all">All teams</option>
              {teams.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              className="lineup-pill-select"
              value={squadFilter}
              onChange={(e) => setSquadFilter(e.target.value)}
            >
              <option value="all">All squads</option>
              {squads.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className="lineup-pill-select"
              value={ageGroupFilter}
              onChange={(e) => setAgeGroupFilter(e.target.value)}
            >
              <option value="all">All age groups</option>
              {ageGroups.map((ag) => (
                <option key={ag} value={ag}>
                  {ag}
                </option>
              ))}
            </select>
            <select
              className="lineup-pill-select"
              value={practiceTypeFilter}
              onChange={(e) => setPracticeTypeFilter(e.target.value)}
            >
              <option value="all">All types</option>
              <option value="practice">Practice</option>
              <option value="regatta">Regatta</option>
              <option value="event">Event</option>
            </select>
            <div className="date-range-filter">
              <label className="date-range-filter-label">
                From
                <input
                  type="date"
                  className="input"
                  value={dateRangeFrom}
                  onChange={(e) => setDateRangeFrom(e.target.value)}
                />
              </label>
              <label className="date-range-filter-label">
                To
                <input
                  type="date"
                  className="input"
                  value={dateRangeTo}
                  onChange={(e) => setDateRangeTo(e.target.value)}
                />
              </label>
              <button
                className="btn ghost"
                type="button"
                onClick={() => {
                  if (!dateRangeFrom || !dateRangeTo) return;
                  const from = dateRangeFrom <= dateRangeTo ? dateRangeFrom : dateRangeTo;
                  const to = dateRangeFrom <= dateRangeTo ? dateRangeTo : dateRangeFrom;
                  setRangeStart(from);
                  setRangeEnd(to);
                  setShowFilters(false);
                }}
              >
                Apply
              </button>
            </div>
            <button
              className="btn-clear-filters"
              onClick={() => {
                setRoleFilter("all");
                setTeamFilter(coach?.team || "all");
                setSquadFilter(coach?.squad || "all");
                setAgeGroupFilter("all");
                setPracticeTypeFilter("all");
                const today = toLocalYmd();
                setDateRangeFrom(today);
                setDateRangeTo("");
                setRangeStart(today);
                setRangeEnd(null);
              }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {attendanceError && (
        <div className="card" style={{ padding: 12 }}>
          <span className="help" style={{ color: "red" }}>{attendanceError}</span>
        </div>
      )}

      {!loadingAttendance && !rosterLoading && (
        <div className="card attendance-card">
          
          <div className="attendance-table-wrapper" ref={tableRef}>
            <div className="attendance-grid">
              <div className="attendance-row attendance-header-row">
                <div className="attendance-name-col">Athlete</div>
                {practiceStatuses.map(({ id, info, responseCounts, typeClass }) => {
                  const control = columnControls[id] || {};
                  const hasFilter = control.filter && control.filter !== "all";
                  const hasSort = control.sort && control.sort !== "none";

                  return (
                    <div
                      key={id}
                      className={`attendance-practice-col attendance-practice-header ${typeClass}`}
                      title={info.title}
                      ref={(el) => (dropdownRefs.current[id] = el)}
                    >
                      <div className="practice-header-content">
                        <div className="practice-labels">
                          <div className="practice-label-day">{info.day}</div>
                          <div className="practice-label-date">{info.md}</div>
                          <div className="practice-label-date">{info.time}</div>
                          {(responseCounts.C > 0 || responseCounts.R > 0 || responseCounts.NR > 0) && (
                            <div className="practice-position-counts">
                              {responseCounts.C > 0 && (
                                <div className="position-count">C: {responseCounts.C}</div>
                              )}
                              {responseCounts.R > 0 && (
                                <div className="position-count">R: {responseCounts.R}</div>
                              )}
                              {responseCounts.NR > 0 && (
                                <div className="position-count">NR: {responseCounts.NR}</div>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          className={`practice-filter-btn ${hasFilter || hasSort ? "active" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === id ? null : id);
                          }}
                          title="Filter/Sort"
                        >
                          &#9660;
                        </button>
                        {activeDropdown === id && (
                          <div className="practice-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                            <div className="filter-section">
                              <div className="filter-label">Filter:</div>
                              <label>
                                <input
                                  type="radio"
                                  name={`filter-${id}`}
                                  checked={!control.filter || control.filter === "all"}
                                  onChange={() => {
                                    setColumnControls((prev) => ({
                                      ...prev,
                                      [id]: { ...prev[id], filter: "all" },
                                    }));
                                  }}
                                />
                                All
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={`filter-${id}`}
                                  checked={control.filter === "available"}
                                  onChange={() => {
                                    setColumnControls((prev) => ({
                                      ...prev,
                                      [id]: { ...prev[id], filter: "available" },
                                    }));
                                  }}
                                />
                                Available
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={`filter-${id}`}
                                  checked={control.filter === "notavailable"}
                                  onChange={() => {
                                    setColumnControls((prev) => ({
                                      ...prev,
                                      [id]: { ...prev[id], filter: "notavailable" },
                                    }));
                                  }}
                                />
                                Not Available
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={`filter-${id}`}
                                  checked={control.filter === "noresponse"}
                                  onChange={() => {
                                    setColumnControls((prev) => ({
                                      ...prev,
                                      [id]: { ...prev[id], filter: "noresponse" },
                                    }));
                                  }}
                                />
                                No Response
                              </label>
                            </div>
                            <div className="filter-section">
                              <div className="filter-label">Sort:</div>
                              <label>
                                <input
                                  type="radio"
                                  name={`sort-${id}`}
                                  checked={!control.sort || control.sort === "none"}
                                  onChange={() => {
                                    setColumnControls((prev) => ({
                                      ...prev,
                                      [id]: { ...prev[id], sort: "none" },
                                    }));
                                  }}
                                />
                                None
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={`sort-${id}`}
                                  checked={control.sort === "desc"}
                                  onChange={() => {
                                    // Clear other column sorts
                                    setColumnControls((prev) => {
                                      const updated = {};
                                      Object.keys(prev).forEach((key) => {
                                        updated[key] = { ...prev[key], sort: "none" };
                                      });
                                      updated[id] = { ...prev[id], sort: "desc" };
                                      return updated;
                                    });
                                  }}
                                />
                                Available First
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={`sort-${id}`}
                                  checked={control.sort === "asc"}
                                  onChange={() => {
                                    // Clear other column sorts
                                    setColumnControls((prev) => {
                                      const updated = {};
                                      Object.keys(prev).forEach((key) => {
                                        updated[key] = { ...prev[key], sort: "none" };
                                      });
                                      updated[id] = { ...prev[id], sort: "asc" };
                                      return updated;
                                    });
                                  }}
                                />
                                Not Available First
                              </label>
                            </div>
                            <button
                              className="clear-filter-btn"
                              onClick={() => {
                                setColumnControls((prev) => {
                                  const updated = { ...prev };
                                  delete updated[id];
                                  return updated;
                                });
                                setActiveDropdown(null);
                              }}
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {practiceStatuses.length === 0 ? (
                <div className="attendance-empty">No practices in this range.</div>
              ) : (
                <>
                  {displayedRoster.map((athlete) => {
                    const userId = athlete.user_id ?? athlete.id;
                    return (
                      <div key={userId} className="attendance-row">
                        <div className="attendance-name-col">
                          <div className="roster-name">
                            <span
                              onClick={() => setSelectedUser(athlete)}
                              style={{ cursor: "pointer", textDecoration: "underline" }}
                            >
                              {athlete.name || "—"}
                            </span>
                            {canonicalRole(athlete) && (
                              <span
                                className={`roster-role-chip roster-role-${canonicalRole(athlete)}`}
                              >
                                {athlete.role ||
                                  (canonicalRole(athlete) === "coach" ? "Coach" : "Athlete")}
                              </span>
                            )}
                          </div>
                          <div className="attendance-meta">
                            {athlete.team && <span>{athlete.team}</span>}
                            {athlete.position && (
                              <span style={{ marginLeft: athlete.team ? 6 : 0 }}>
                                {athlete.team && " • "}
                                {athlete.position}
                              </span>
                            )}
                            {athlete.squad && (
                              <span className="help" style={{ marginLeft: 6 }}>
                                {athlete.squad}
                              </span>
                            )}
                          </div>
                        </div>
                        {practiceStatuses.map(({ id, statuses, typeClass }) => {
                          const status = statuses[userId] || null;
                          const display = statusDisplay(status);
                          const cellKey = `${id}-${userId}`;
                          const isSaving = savingCell === cellKey;
                          const isCoach = canonicalRole(athlete) === "coach";
                          const canEdit = editMode;

                          return (
                            <button
                              type="button"
                              key={cellKey}
                              className={`attendance-practice-col attendance-status attendance-cell-btn ${display.className} ${typeClass}`}
                              title={`${display.label}${canEdit ? " — click to update" : ""}`}
                              onClick={() => {
                                if (!canEdit || isSaving) return;
                                updateAttendanceStatus(id, userId, isCoach);
                              }}
                              disabled={!canEdit || isSaving}
                            >
                              {isSaving ? "…" : display.icon}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                  {displayedRoster.length === 0 && (
                    <div className="attendance-empty">
                      No roster members match your filters.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {loadingMore && (
            <div className="attendance-loading-more">Loading more practices…</div>
          )}
        </div>
      )}

      <UserModal
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
        onUpdated={onRosterUpdated}
        onShowUser={(u) => setSelectedUser(u)}
        canEditRoster={coach?.can_edit_roster ?? false}
        canManageUsers={coach?.permissions?.includes?.("users:manage") ?? coach?.can_manage_users ?? false}
        roster={roster}
        teams={teams}
        squads={squads}
        positions={positions}
        highSchools={highSchools}
      />
    </div>
  );
}
